import { useState } from "react";
import { Search, Loader2, Plus, X, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

interface BulkUrlInputProps {
  onScan: (urls: string[]) => void;
  isLoading: boolean;
  bulkLimit: number;
  creditsAvailable: number;
}

export const BulkUrlInput = ({ onScan, isLoading, bulkLimit, creditsAvailable }: BulkUrlInputProps) => {
  const [urls, setUrls] = useState<string[]>([""]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const { language } = useLanguage();

  const handleSingleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validUrls = urls.filter(u => u.trim());
    if (validUrls.length > 0) {
      onScan(validUrls);
    }
  };

  const handleBulkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedUrls = bulkText
      .split(/[\n,]/)
      .map(u => u.trim())
      .filter(u => u.length > 0);
    
    if (parsedUrls.length > 0) {
      onScan(parsedUrls.slice(0, Math.min(bulkLimit, creditsAvailable)));
    }
  };

  const addUrlField = () => {
    if (urls.length < bulkLimit) {
      setUrls([...urls, ""]);
    }
  };

  const removeUrlField = (index: number) => {
    if (urls.length > 1) {
      setUrls(urls.filter((_, i) => i !== index));
    }
  };

  const updateUrl = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const validUrlCount = isBulkMode 
    ? bulkText.split(/[\n,]/).filter(u => u.trim()).length
    : urls.filter(u => u.trim()).length;

  const effectiveLimit = Math.min(bulkLimit, creditsAvailable);
  const isOverLimit = validUrlCount > effectiveLimit;

  const getText = (key: 'placeholder' | 'button' | 'analyzing' | 'bulkMode' | 'singleMode' | 'urlsCount' | 'bulkPlaceholder' | 'addUrl' | 'limitInfo') => {
    const texts = {
      fr: {
        placeholder: 'Entrez une URL à analyser...',
        button: 'Analyser',
        analyzing: 'Analyse en cours...',
        bulkMode: 'Mode lot',
        singleMode: 'Mode simple',
        urlsCount: 'URLs',
        bulkPlaceholder: 'Entrez vos URLs (une par ligne ou séparées par des virgules)...',
        addUrl: 'Ajouter une URL',
        limitInfo: `Max ${effectiveLimit} URLs`,
      },
      en: {
        placeholder: 'Enter a URL to analyze...',
        button: 'Analyze',
        analyzing: 'Analyzing...',
        bulkMode: 'Bulk mode',
        singleMode: 'Single mode',
        urlsCount: 'URLs',
        bulkPlaceholder: 'Enter your URLs (one per line or comma-separated)...',
        addUrl: 'Add URL',
        limitInfo: `Max ${effectiveLimit} URLs`,
      },
      es: {
        placeholder: 'Ingresa una URL para analizar...',
        button: 'Analizar',
        analyzing: 'Analizando...',
        bulkMode: 'Modo lote',
        singleMode: 'Modo simple',
        urlsCount: 'URLs',
        bulkPlaceholder: 'Ingresa tus URLs (una por línea o separadas por comas)...',
        addUrl: 'Agregar URL',
        limitInfo: `Máx ${effectiveLimit} URLs`,
      },
    };
    return texts[language]?.[key] || texts.en[key];
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-3">
      {/* Mode toggle */}
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsBulkMode(!isBulkMode)}
          className="text-muted-foreground hover:text-foreground"
        >
          <List className="w-4 h-4 mr-2" />
          {isBulkMode ? getText('singleMode') : getText('bulkMode')}
        </Button>
        <div className="flex items-center gap-2">
          <Badge 
            variant={isOverLimit ? "destructive" : "secondary"}
            className={cn(
              "text-xs",
              !isOverLimit && "bg-primary/10 text-primary"
            )}
          >
            {validUrlCount} {getText('urlsCount')}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {getText('limitInfo')}
          </Badge>
        </div>
      </div>

      {isBulkMode ? (
        /* Bulk mode - textarea */
        <form onSubmit={handleBulkSubmit} className="space-y-3">
          <div className="relative p-2 bg-secondary/50 rounded-xl border border-border/50 glow-border transition-all focus-within:border-primary/50">
            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={getText('bulkPlaceholder')}
              className="min-h-[120px] border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground placeholder:text-muted-foreground resize-none"
              disabled={isLoading}
            />
          </div>
          <Button
            type="submit"
            disabled={isLoading || validUrlCount === 0 || isOverLimit}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6 py-2 rounded-lg transition-all"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {getText('analyzing')}
              </>
            ) : (
              <>
                {getText('button')} ({Math.min(validUrlCount, effectiveLimit)} {getText('urlsCount')})
              </>
            )}
          </Button>
        </form>
      ) : (
        /* Single/multiple mode - individual inputs */
        <form onSubmit={handleSingleSubmit} className="space-y-3">
          {urls.map((url, index) => (
            <div 
              key={index} 
              className="relative flex items-center gap-2 p-2 bg-secondary/50 rounded-xl border border-border/50 glow-border transition-all focus-within:border-primary/50"
            >
              <div className="flex-1 flex items-center gap-3 pl-4">
                <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => updateUrl(index, e.target.value)}
                  placeholder={getText('placeholder')}
                  className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground placeholder:text-muted-foreground"
                  disabled={isLoading}
                />
              </div>
              {urls.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeUrlField(index)}
                  className="text-muted-foreground hover:text-destructive"
                  disabled={isLoading}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          
          <div className="flex gap-2">
            {urls.length < effectiveLimit && (
              <Button
                type="button"
                variant="outline"
                onClick={addUrlField}
                disabled={isLoading}
                className="flex-1"
              >
                <Plus className="w-4 h-4 mr-2" />
                {getText('addUrl')}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isLoading || validUrlCount === 0}
              className={cn(
                "bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6 py-2 rounded-lg transition-all",
                urls.length < effectiveLimit ? "flex-1" : "w-full"
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {getText('analyzing')}
                </>
              ) : (
                <>
                  {getText('button')} {validUrlCount > 1 && `(${validUrlCount})`}
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
